import React from "react";
import { useLocation } from "react-router-dom";
import AuthBar from "../AuthBar";

const PAGE_META = {
  "/studio": {
    title: "Studio",
    subtitle: "Deploy, import, and operate smart contracts from one workspace.",
  },
  "/about": {
    title: "About KRIONEX",
    subtitle: "The operating layer for EVM contract deployment, verification, and post-launch operations.",
  },
  "/guide": {
    title: "User Guide",
    subtitle: "Learn deployment flows, NFT metadata, custom Solidity, ownership, roles, and safe contract operations.",
  },
  "/dashboard": {
    title: "Operations",
    subtitle: "Monitor contract activity, workflows, and operational status.",
  },
  "/operations": {
    title: "Operations",
    subtitle: "Run token actions and manage post-launch contract workflows.",
  },
  "/deployments": {
    title: "Deployments",
    subtitle: "Track deployment records, status, and verification progress.",
  },
  "/history": {
    title: "History",
    subtitle: "Review contract activity, wallet actions, and deployment events.",
  },
  "/upgrade": {
    title: "Upgrade",
    subtitle: "Manage plan access, usage limits, and workspace billing.",
  },
  "/account": {
    title: "Account Settings",
    subtitle: "Manage your profile, billing email, plan details, and wallet-linked access.",
  },
  "/help-center": {
    title: "Support Center",
    subtitle: "Contact Krionex support for deployment, verification, billing, or app issues.",
  },
  "/examples": {
    title: "Examples",
    subtitle: "Review example deployments and common contract workflows.",
  },
  "/trust": {
    title: "Trust & Security",
    subtitle: "Understand wallet-signed execution, platform boundaries, and security practices.",
  },
  "/terms": {
    title: "Terms",
    subtitle: "Review the terms that govern use of Krionex.",
  },
  "/privacy": {
    title: "Privacy",
    subtitle: "Understand how Krionex handles privacy and data.",
  },
  "/refund-policy": {
    title: "Refund Policy",
    subtitle: "Review subscription and refund policy details.",
  },
  "/contact": {
    title: "Contact",
    subtitle: "Reach Krionex for general questions and contact requests.",
  },
  "/admin": {
    title: "Admin Dashboard",
    subtitle: "Manage users, deployments, and platform oversight.",
  },
};

function getPageMeta(pathname) {
  if (pathname.startsWith("/help-center/")) {
    return {
      title: "Support Ticket",
      subtitle: "View ticket details and support progress.",
    };
  }

  if (pathname.startsWith("/token/")) {
    return {
    title: "Contract Dashboard",
    subtitle: "Inspect contract details, ownership, roles, balances, and supported operations.",
    };
  }

  return (
    PAGE_META[pathname] || {
      title: "KRIONEX",
      subtitle: "The operating layer for EVM token contracts.",
    }
  );
}

function accessTone(accessLabel) {
  const value = String(accessLabel || "").trim().toLowerCase();

  if (value === "owner") {
    return "border-[#E8D7AD] bg-[#FFF9EA] text-[#8C6722]";
  }

  if (value === "admin") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }

  if (value === "pro") {
    return "border-[#E8D7AD] bg-[#FFF9EA] text-[#8C6722]";
  }

  if (value === "team") {
    return "border-violet-200 bg-violet-50 text-violet-700";
  }

  return "border-slate-200 bg-white text-slate-700";
}

export default function Topbar({ accessLabel = "", onOpenSidebar }) {
  const location = useLocation();
  const meta = getPageMeta(location.pathname);

  return (
    <header className="z-30 shrink-0 border-b border-slate-200/80 bg-white/88 shadow-[0_10px_35px_rgba(15,23,42,0.035)] backdrop-blur-2xl">
      <div className="flex min-h-[84px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-10">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onOpenSidebar}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-[#050713] shadow-sm transition hover:border-[#E8D7AD] hover:bg-[#FFF9EA] lg:hidden"
            aria-label="Open navigation"
          >
            <span className="text-xl leading-none">☰</span>
          </button>

          <div className="min-w-0">
            <div className="mb-1 hidden text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A87E2C] sm:block">
              Krionex workspace
            </div>
            <h1 className="truncate text-2xl font-semibold tracking-[-0.035em] text-[#050713] sm:text-3xl">
              {meta.title}
            </h1>
            <p className="mt-1 hidden truncate text-sm leading-6 text-slate-500 sm:block">
              {meta.subtitle}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {accessLabel ? (
            <div
              className={`hidden rounded-full border px-3.5 py-1.5 text-xs font-semibold md:block ${accessTone(
                accessLabel,
              )}`}
            >
              {accessLabel}
            </div>
          ) : null}
          <AuthBar />
        </div>
      </div>
    </header>
  );
}